import React, { useEffect, useState } from 'react';
import { Avatar, Button, Card, Col, Divider, List, Row, Typography, message } from 'antd';
import axios from 'axios';
import { format } from 'date-fns';
import { BsStarFill, BsStarHalf } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import ContentLoader from 'react-content-loader';
import CompanyName from 'components/UtilComponents/CompanyName';
import DetailsGrid from 'components/UtilComponents/DetailsGrid';
import RateOrder from 'components/Modals/RateOrder';
import { OPEN_MERCHANT_NAME, ORDER_URL } from 'constants/ApiConstants';
import { formatQuantity, formatRefNo } from 'utils/formatter';
import EmptyContent from 'components/UtilComponents/EmptyContent';
import IconOrders from 'assets/images/orders.png';
import CreateNewOpenRFQ from 'components/Modals/Buy/CreateNewOpenRFQ';
import { getBreakPoint } from 'utils/layout';
import { isEmpty } from 'lodash';

const { Text, Title } = Typography;

const CompletedOrders = ({ tab }) => {
  const isMobile = !getBreakPoint().includes('sm');
  const isDesktop = !getBreakPoint().includes('xl');
  const navigate = useNavigate();
  const [showOpenRFQ, setShowOpenRFQ] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState({});
  const [rateOrderVisible, setRateOrderVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 8,
    total: 0,
    size: 'middle',
    showSizeChanger: false,
    className: isMobile && 'custom-pagination',
  });

  useEffect(() => {
    if (tab === 'completed-orders') {
      onLoadOrders();
    } else {
      setPagination({ ...pagination, current: 1 });
    }
  }, [tab, pagination.current]);

  const onLoadOrders = async () => {
    setLoading(true);
    try {
      let currentPage = pagination.current;
      const localData = await JSON.parse(localStorage.getItem('filters'));
      if (!isEmpty(localData)) {
        currentPage = localData.current;
      }

      const res = await axios.get(`${ORDER_URL}?page=${currentPage}&limit=${pagination.pageSize}&populate=true`, {
        headers: {
          'X-API-Filters': JSON.stringify({ buyer: ['me'], status: ['Preclosed', 'Completed'] }),
          'X-API-Sort': JSON.stringify({ createdAt: 'desc' }),
        },
      });

      if (res.data.status === 'error') {
        message.error(res.data.error);
      } else {
        setOrders(res.data.orders);
        setPagination({
          ...pagination,
          current: currentPage,
          total: res.data.count,
        });
      }
      localStorage.removeItem('filters');
    } catch (err) {
      message.error(err.message);
    }
    setLoading(false);
  };

  const onViewOrders = (orderId) => {
    localStorage.setItem('filters', JSON.stringify({ current: pagination.current }));
    navigate(`/buy/orders/completed-orders/${orderId}`);
  };

  const showRateOrder = (reload) => {
    if (reload) {
      onLoadOrders();
    }
    setRateOrderVisible(!rateOrderVisible);
  };

  const onCreateNewOpenRFQ = () => {
    setShowOpenRFQ(!showOpenRFQ);
  };

  if (loading) {
    return (
      <ContentLoader width="100%" height="70vh" backgroundColor="#d9d9d9" foregroundColor="#ededed">
        <rect x="0" y="25" rx="10" ry="10" width="48%" height="211" />
        <rect x="50%" y="25" rx="10" ry="10" width="50%" height="211" />
        <rect x="0" y="250" rx="10" ry="10" width="48%" height="211" />
        <rect x="50%" y="250" rx="10" ry="10" width="50%" height="211" />
      </ContentLoader>
    );
  }

  return (
    <>
      {orders?.length > 0 ? (
        <List
          className="my-4"
          header={null}
          footer={null}
          grid={{ gutter: [16, 4], column: isMobile ? 1 : 2 }}
          dataSource={orders}
          pagination={{ ...pagination, onChange: (page) => setPagination({ ...pagination, current: page }) }}
          renderItem={(order) => {
            const { product } = order;
            return (
              <>
                <List.Item>
                  <Card size={isMobile ? 'small' : 'default'}>
                    <Row justify="flex-start" align="top">
                      <Col flex={1} span={16}>
                        <Title level={5} className="fw-medium">
                          {product?.name}
                        </Title>
                        <Text>
                          {formatQuantity(order?.qty) || '∞'} {order?.subUnit} | {formatRefNo(order?.orderRefNo)}
                        </Text>
                        <CompanyName name={OPEN_MERCHANT_NAME} verified={true} />
                      </Col>
                      <Col flex={1} className="d-flex justify-content-end">
                        <Button type="link" onClick={() => onViewOrders(order?._id)}>
                          View {!isMobile && 'Order'}
                        </Button>
                      </Col>
                    </Row>

                    <Row gutter={16} className="mt-3">
                      <Col span={isDesktop ? 24 : 12}>
                        <DetailsGrid icon={<Avatar src={require('assets/images/icon-order-status.png')} shape="square" size="small" />} title={'Order Status'} description={order?.status} />
                      </Col>
                      <Col span={isDesktop ? 24 : 12}>
                        <DetailsGrid
                          icon={<Avatar src={require('assets/images/icon-calendar.png')} shape="square" size="small" />}
                          title={'Delivery Date'}
                          description={order?.deliveryOn ? format(new Date(order?.deliveryOn), 'dd MMM yyyy') : ''}
                        />
                      </Col>
                    </Row>

                    <Divider dashed={true} className="my-3" />

                    <Row gutter={16} align="middle">
                      {false && (
                        <Col span={12}>
                          {/* TODO: integrate resend open rfq */}
                          <Button type="primary" block onClick={() => {}}>
                            Resend RFQ
                          </Button>
                        </Col>
                      )}

                      {!order?.metadata?.sellerRating && (
                        <Col span={12}>
                          <Button
                            type="primary"
                            ghost
                            block
                            onClick={() => {
                              showRateOrder();
                              setSelectedOrder(order);
                            }}>
                            Rate Order
                          </Button>
                        </Col>
                      )}
                      {order?.metadata?.sellerRating && (
                        <Col span={12}>
                          <Row align="middle">
                            <Text>Rating: &nbsp;</Text>
                            {order?.metadata?.sellerRating === 5 ? <BsStarFill style={{ color: '#ffc53d', fontSize: '18px' }} /> : <BsStarHalf style={{ color: '#ffc53d', fontSize: '18px' }} />}
                            &nbsp;
                            <Text>{order?.metadata?.sellerRating} / 5</Text>
                          </Row>
                        </Col>
                      )}
                    </Row>
                  </Card>
                </List.Item>
              </>
            );
          }}
        />
      ) : (
        <EmptyContent source={IconOrders} title={'No Completed Orders'} subTitle={'There are no completed orders found right now.'} />
      )}
      <CreateNewOpenRFQ visible={showOpenRFQ} close={onCreateNewOpenRFQ} />
      <RateOrder type="seller" order={selectedOrder} visible={rateOrderVisible} close={showRateOrder} />
    </>
  );
};

export default CompletedOrders;
